import Vue from 'vue'
import Router from 'vue-router'
// import Checkout from '@/views/Checkout'
// import Admin from '@/views/Admin'
import store from '@/store'
import { v2Enabled } from '@/utils/featureFlags'
// The two pages Google indexes are in the main bundle, not in async chunks (they were lazy from
// 2026-10-05): when Google's renderer fails to fetch a route chunk – it often skips resources –
// the first navigation is aborted and Vue Router puts the URL back to '/'. Google filed that as
// a redirect to the front page and dropped ~10 000 listings from the index by 2026-10-23.
import Home from './views/Home.vue'
import Ad from './views/Ad.vue'
Vue.use(Router)

// Wait for the session bootstrap before deciding, so a direct link or reload
// isn't redirected just because getMe/getMyAds haven't returned yet.
function sessionGuard (decide) {
  return (to, from, next) => {
    store.dispatch('initSession').then(() => next(decide(to)))
  }
}

function toMyAdsIfSeveral () {
  return store.getters.myAds.length > 1 ? { name: 'my-ads' } : undefined
}

// Sends guests to the login page remembering where they were headed, so a
// direct link (an email link, say) still works after they log in.
function requireLogin (to) {
  return store.getters.user
    ? undefined
    : { name: 'login', query: { redirect: to.fullPath } }
}

// The photos page edits an ad, so only its owner may open it. This is a UX
// guard on top of the API, not a security boundary.
function requireOwnAdPhotos (to) {
  const login = requireLogin(to)
  if (login) {
    return login
  }
  const owned = store.getters.myAds.some(ad =>
    String(ad.id) === String(to.params.adid) &&
    (ad.flats || []).some(flat => String(flat.id) === String(to.params.flatid))
  )
  return owned ? undefined : { name: 'ad', params: { id: to.params.adid } }
}

// The member pages (/annonser, /matchningar, ...) all need a logged-in
// visitor with exactly one listing: guests keep their link through the login
// page, members without a listing are sent to create one.
function requireMember (to) {
  const login = requireLogin(to)
  if (login) {
    return login
  }
  if (!store.getters.myAds.length) {
    return { name: 'create-ad' }
  }
  return toMyAdsIfSeveral()
}

// Same, but the v2 pages are hidden unless the v2 flag is set locally.
function requireV2Member (to) {
  return v2Enabled() ? requireMember(to) : { name: 'home' }
}

// Vue Router 3 rejects navigation promises with undefined when navigation is
// cancelled or blocked by a guard. Patch the prototype once to suppress these
// unhandled rejections globally, instead of adding .catch() to every push call.
const originalPush = Router.prototype.push
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(function() {})
}

const originalReplace = Router.prototype.replace
Router.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(function() {})
}

export default new Router({
  mode: 'history',
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  base: process.env.BASE_URL,
  linkExactActiveClass: 'active',
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home
    },
    {
      path: '/historik',
      name: 'history',
      component: () => import('./views/History.vue'),
      beforeEnter: sessionGuard(requireMember)
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('./views/About.vue')
    },
    {
      name: 'ad',
      path: '/bostad/:id',
      component: Ad,
      props: true
    },
    {
      name: 'adlist',
      path: '/annonser',
      component: () => import('./views/AdList.vue'),
      beforeEnter: sessionGuard(requireMember)
    },
    {
      name: 'adlist-grid',
      path: '/annonser-grid',
      component: () => import('./views/AdListGrid.vue'),
      beforeEnter: sessionGuard(requireMember)
    },
    {
      name: 'my-ads',
      path: '/mina-annonser',
      component: () => import('./views/MyAds.vue')
    },
    {
      name: 'terms',
      path: '/villkor',
      component: () => import('./views/Terms.vue')
    },
    {
      name: 'cookies',
      path: '/cookiepolicy',
      component: () => import('./views/Cookies.vue')
    },
    // {
    //   name: 'jobs',
    //   path: '/jobb',
    //   component: WorkHere
    // },
    {
      name: 'about-us',
      path: '/om-oss',
      component: () => import('./views/AboutUs.vue')
    },
    {
      name: 'create-ad',
      path: '/skapa-annons',
      component: () => import('./views/AdCreate.vue')
    },
    {
      name: 'offer',
      path: '/erbjudande',
      component: () => import('./views/Erbjudande.vue')
    },
    // {
    //   name: 'admin',
    //   path: '/admin',
    //   component: Admin
    // },
    {
      name: 'my-filters',
      path: '/onskemal',
      component: () => import('./views/Wishes.vue'),
      beforeEnter: sessionGuard(requireMember)
    },
    {
      name: 'resetpw',
      path: '/nytt-losenord/:uuid',
      component: () => import('@/views/ResetPw'),
      props: true
    },
    {
      name: 'login',
      path: '/login',
      component: () => import('./views/Login.vue')
    },
    {
      name: 'my-account',
      path: '/mitt-konto',
      component: () => import('./views/MyAccount.vue'),
      beforeEnter: sessionGuard(requireLogin)
    },
    {
      name: 'delete-data',
      path: '/radera-data',
      component: () => import('./views/DeleteData.vue')
    },
    {
      name: 'lagenhetsbyte',
      path: '/lagenhetsbyte',
      component: () => import('./views/Lagenhetsbyte.vue')
    },
    {
      name: 'checkout',
      path: '/betala',
      component: () => import('./views/Checkout.vue')
    },
    {
      name: 'thanks',
      path: '/klart',
      component: () => import('./views/Thanks.vue')
    },
    {
      name: '360-viewing',
      path: '/360-visning',
      component: () => import('./views/360-viewing.vue')
    },
    {
      name: 'matches',
      path: '/matchningar',
      component: () => import('./views/Matches.vue'),
      beforeEnter: sessionGuard(requireMember)
    },
    {
      name: 'bytesforslag',
      path: '/bytesforslag',
      component: () => import('./views/Bytesforslag.vue'),
      beforeEnter: sessionGuard(requireV2Member)
    },
    {
      name: 'bytesforslag-detail',
      path: '/bytesforslag/:id',
      component: () => import('./views/BytesforslagDetail.vue'),
      props: true,
      beforeEnter: sessionGuard(requireV2Member)
    },
    {
      name: 'v2-matches',
      path: '/mina-byten',
      component: () => import('./views/V2Matches.vue'),
      beforeEnter: sessionGuard(requireV2Member)
    },
    {
      name: 'newad',
      path: '/newad',
      component: () => import('@/components/AdForm.vue')
    },
    {
      name: 'faq',
      path: '/faq',
      component: () => import('./views/FAQ.vue')
    },
    {
      name: 'ad-photos',
      path: '/bostad/:adid/:flatid/bilder',
      component: () => import('./views/AdPhotos.vue'),
      props: true,
      beforeEnter: sessionGuard(requireOwnAdPhotos)
    },
    {
      name: 'checkup',
      path: '/kontroll/:kontrollid',
      component: () => import('./views/Checkup.vue'),
      props: true
    },
    {
      name: '404',
      path: '/404',
      component: () => import('./views/404.vue')
    },
    { path: '*', redirect: '/404' }
  ]
})
